import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  DatePicker,
  Form,
  Input,
  Tooltip,
  Select,
  Checkbox,
  Button,
  Divider,
  Typography,
  Space,
} from 'antd';
import { QuestionCircleOutlined, MobileOutlined, GlobalOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

import FirebaseSetup from '../../FirebaseSetup';
import { FormItemLayout, TailFormItemLayout } from '../../utils/Constants';

import { ClientCategoryFormItem, categoriesEN } from './ClientCategoryFormItem';
import CategoryPillGrid from './CategoryPillGrid';
import NativeSelect from './NativeSelect';
import SsnLast4Input from './SsnLast4Input';
import SignupPhoneVerify from './SignupPhoneVerify';

const { Option } = Select;
const { Text } = Typography;

const firebaseSetup = new FirebaseSetup();

export const countryByCountryCode = {
  'US' : 'United States',
  'IE' : 'Ireland',
  'GB' : 'United Kingdom',
  // Eurozone EU
  'AT' : 'Austria',
  'BE' : 'Belgium',
  'CY' : 'Cyprus',
  'EE' : 'Estonia',
  'ES' : 'Spain',
  'FI' : 'Finland',
  'FR' : 'France',
  'DE' : 'Germany',
  'GR' : 'Greece',
  'HR' : 'Croatia',
  'IT' : 'Italy',
  'LV' : 'Latvia',
  'LT' : 'Lithuania',
  'LU' : 'Luxembourg',
  'MT' : 'Malta',
  'NL' : 'Netherlands',
  'PT' : 'Portugal',
  'SK' : 'Slovakia',
  'SI' : 'Slovenia',
  // Non-eurozone EU + EEA non-EU
  'BG' : 'Bulgaria',
  'CZ' : 'Czechia',
  'DK' : 'Denmark',
  'HU' : 'Hungary',
  'IS' : 'Iceland',
  'NO' : 'Norway',
  'PL' : 'Poland',
  'RO' : 'Romania',
  'SE' : 'Sweden',
};

const phonePlaceHolderByCountryCode = {
  'IE' : '+353881234567',
  'GB' : '+441234567890',
  'US' : '6505551234',
  'AT' : '+431234567890',
  'BE' : '+3212345678',
  'CY' : '+35712345678',
  'EE' : '+3721234567',
  'FI' : '+358123456789',
  'FR' : '+33 6 12 34 56 78',
  'DE' : '+491234567890',
  'GR' : '+302123456789',
  'IT' : '+391234567890',
  'LV' : '+37112345678',
  'LT' : '+37012345678',
  'LU' : '+35212345678',
  'MT' : '+35612345678',
  'NL' : '+31 6 12345678',
  'PT' : '+351 912 345 678',
  'SK' : '+421123456789',
  'SI' : '+38612345678',
  'ES' : '+34 612 345 678',
};

const availableCountryCodes = [
  'US', 'GB', 'IE',
  // Eurozone EU
  'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'HR', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
  // Non-eurozone EU + EEA non-EU
  'BG', 'CZ', 'DK', 'HU', 'IS', 'NO', 'PL', 'RO', 'SE',
];
const countriesSupportingBusiness = ['IE', 'GB'];
const countriesNotSupportingBusiness = ['US'];
const countriesRequiringSSN = ['US'];

export const AdminCheckBoxFormItems = ({ isAdmin, title, isViewOnly, client }) => {
  const showCheckBoxes = isAdmin && !isViewOnly;
  const showListOfItems = isAdmin && !!isViewOnly;

  const adminCheckBoxes = [
    {
      key: 'isTransferToTeamAllowed',
      label: 'Allow balance transfer to teams',
      unsetLabel: 'No transfer to teams',
      rules: [{ required: false, }]
    },
    {
      key: 'shouldNotPauseTags',
      label: 'Should not pause pages',
      unsetLabel: 'May pause pages',
      rules: [{ required: false, }]
    },
    {
      key: 'isNewActivity',
      label: 'Use new recent history layout (temp)',
      unsetLabel: 'Use old recent history layout (temp)',
      rules: [{ required: false, }]
    },
    {
      key: 'isDeveloper',
      label: 'Is a partner',
      unsetLabel: 'Is not a partner',
      rules: [{ required: false, }]
    },
    {
      key: 'isCompany',
      label: 'Is a company',
      unsetLabel: 'Is an individual',
      rules: [{ required: false, }]
    },
    {
      key: 'isShowAds',
      label: 'Display banner ads',
      unsetLabel: 'Disable banner ads',
      rules: [{ required: false, }]
    },
    {
      key: 'isOldTeamSettings',
      label: 'Use old team settings',
      unsetLabel: 'Use new team settings',
      rules: [{ required: false, }]
    },
  ];

  const formatLoginMethod = (authProvider) => {
    if (!authProvider) return 'Unknown';
    const normalized = `${authProvider}`.toLowerCase();
    if (normalized === 'google') return 'Google';
    if (normalized === 'apple') return 'Apple';
    if (normalized === 'email') return 'Email';
    if (normalized === 'phone') return 'Phone';
    if (normalized === 'anonymous') return 'Anonymous';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const getPlatformDisplay = (platform) => {
    if (!platform) return null;
    const normalized = `${platform}`.toLowerCase();
    if (normalized === 'ios') {
      return {
        icon: <MobileOutlined style={{ color: '#8c8c8c' }} />,
        label: 'iOS',
      };
    }
    return {
      icon: <GlobalOutlined style={{ color: '#8c8c8c' }} />,
      label: 'Web',
    };
  };

  return <>
    {isAdmin && (
      <>
        <Divider>{title}</Divider>

        {showCheckBoxes &&
          adminCheckBoxes.map((v) => (
            <Form.Item
              key={`fi-admin-${v.key}`}
              name={v.key}
              valuePropName="checked"
              rules={v.rules}
              {...TailFormItemLayout}
            >
              <Checkbox>
                {v.label}
              </Checkbox>
            </Form.Item>
          ))
        }

        {showListOfItems && client &&
          <Space direction='vertical'>
            {adminCheckBoxes.map((v) => (
              <Text key={`si-admin-${v.key}`} type={(client[v.key]) ? 'success' : 'secondary'}>
                {(client[v.key]) ? v.label : v.unsetLabel}
              </Text>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Space direction="vertical" size={2}>
              <Text strong>Login Method: {formatLoginMethod(client.authProvider)}</Text>
              {client.ownerId && (
                <Text>
                  <a href={`/admin/users/${client.ownerId}`}>View User</a>
                </Text>
              )}
            </Space>
            <Text>Login Email: {client.authEmail || client.email || 'Unknown'}</Text>
            {getPlatformDisplay(client.platform) && (
              <Space size={8}>
                <Text>Platform:</Text>
                {getPlatformDisplay(client.platform).icon}
                <Text>{getPlatformDisplay(client.platform).label}</Text>
              </Space>
            )}
          </Space>
        }

        <Divider />
      </>)}
  </>;

}

function notRequiredWhenAdmin({ isAdmin, required }) {
  return (isAdmin) ? false : required;
}

/**
 * Spain: national number is 9 digits; first digit is 6–9 (mobile 6/7, landline 8/9, etc.).
 * Accepts +34 / 0034 / optional spaces; strips formatting so users aren't blocked by grouping.
 */
function isValidSpanishPhone(value) {
  if (!value || typeof value !== 'string') return false;
  let digits = value.replace(/\D/g, '');
  if (!digits) return false;
  if (digits.startsWith('0034')) digits = digits.slice(4);
  else if (digits.startsWith('34')) digits = digits.slice(2);
  // Domestic format sometimes typed as 0 + 9 digits
  if (digits.startsWith('0') && digits.length === 10) digits = digits.slice(1);
  return digits.length === 9 && /^[6-9]\d{8}$/.test(digits);
}

/** France: 9 digits after +33 (or leading 0 domestic). */
function isValidFrenchPhone(value) {
  if (!value || typeof value !== 'string') return false;
  let d = value.replace(/\D/g, '');
  if (!d) return false;
  if (d.startsWith('0033')) d = d.slice(4);
  else if (d.startsWith('33')) d = d.slice(2);
  if (d.startsWith('0') && d.length === 10) d = d.slice(1);
  return d.length === 9 && /^[1-9]\d{8}$/.test(d);
}

/** Netherlands: 9 digits after +31 (or leading 0 domestic). */
function isValidDutchPhone(value) {
  if (!value || typeof value !== 'string') return false;
  let d = value.replace(/\D/g, '');
  if (!d) return false;
  if (d.startsWith('0031')) d = d.slice(4);
  else if (d.startsWith('31')) d = d.slice(2);
  if (d.startsWith('0') && d.length === 10) d = d.slice(1);
  return d.length === 9 && /^[1-9]\d{8}$/.test(d);
}

/** Portugal: 9 digits after +351. */
function isValidPortuguesePhone(value) {
  if (!value || typeof value !== 'string') return false;
  let d = value.replace(/\D/g, '');
  if (!d) return false;
  if (d.startsWith('00351')) d = d.slice(5);
  else if (d.startsWith('351')) d = d.slice(3);
  if (d.startsWith('0') && d.length === 10) d = d.slice(1);
  return d.length === 9 && /^[1-9]\d{8}$/.test(d);
}

/** Countries where regex-only validation rejected common spaced / +country formats; use digit normalization. */
const PHONE_DIGIT_VALIDATORS = {
  ES: isValidSpanishPhone,
  FR: isValidFrenchPhone,
  NL: isValidDutchPhone,
  PT: isValidPortuguesePhone,
};

/** Two or more words, each with a letter — the same rule the name field validates. */
export const isFullNameValid = (value) => {
  if (!value) return false;
  const words = String(value).trim().split(/\s+/).filter((w) => w.length > 0);
  return words.length >= 2 && words.every((w) => /[a-zA-Z]/.test(w));
};

/** Sign-up can continue once the three required asks are answered. */
export const isSignupFormReady = (values) => !!(
  values && values.country && isFullNameValid(values.displayName) && values.agreeTerms
);

export const EditClientProfileForm = ({ callback, client, isSignUp, isAdmin, ...rest }) => {
  const [form] = Form.useForm();
  const isShowAllFields = (!!isSignUp) ? false : true;
  const { isCompany } = client || { isCompany: false };
  const [isCompanyChecked, setIsCompanyChecked] = useState(isCompany);
  const [selectedCountry, setSelectedCountry] = useState(client && client.country ? client.country : null);
  const [previewText, setPreviewText] = useState(null);
  // Sign-up only: Continue stays disabled until country, a full name and the
  // terms box are all in place, so the button never has to say no.
  const [signupReady, setSignupReady] = useState(false);

  const firebase = firebaseSetup.getFirebase();
  // Bumped after a phone is OTP-verified+linked so verifiedAuthPhone re-reads currentUser.
  const [phoneBump, setPhoneBump] = useState(0);
  // The signed-in user's verified phone CREDENTIAL — only meaningful when editing
  // your OWN account. In admin mode currentUser is the admin (not the client owner),
  // so we ignore it: an admin may override the client's phone as a plain contact
  // field, and that field is NEVER a login credential (saved via model.commit only —
  // no linkWithCredential — and the lookup treats a field-only phone as
  // account_no_credential, so nobody can sign in with it).
  const verifiedAuthPhone = useMemo(() => {
    if (isAdmin) return null;
    const u = firebase.auth().currentUser;
    return u ? (u.phoneNumber || null) : null;
  }, [firebase, phoneBump, isAdmin]);

  // Signup-only: after SignupPhoneVerify links the number, refresh and re-render
  // so the locked, verified phone field replaces the verify widget.
  const onPhoneVerified = useCallback(async () => {
    try { await firebase.auth().currentUser?.reload?.(); } catch (_) { /* noop */ }
    setPhoneBump((b) => b + 1);
  }, [firebase]);

  useEffect(() => {
    if (verifiedAuthPhone) {
      form.setFieldsValue({ phoneNumber: verifiedAuthPhone });
    }
  }, [verifiedAuthPhone, form]);
  /**
   * Ant Design Form resets when `initialValues` gets a new object reference on each parent render.
   * Firestore onSnapshot passes a new `client` object on each event; parent re-renders then passed a new `initialValues` object and the form reset user input.
   * Keep one stable initialValues object per client (clientId or id).
   */
  const initialValuesSeedRef = useRef({ id: null, values: null });
  
  // Calculate preview text helper (shows for all countries when name is valid)
  const updatePreviewText = (displayNameValue, countryValue) => {
    if (!isCompanyChecked && displayNameValue) {
      const words = displayNameValue.trim().split(/\s+/).filter(word => word.length > 0);
      const hasLetterPattern = /[a-zA-Z]/;
      const isValid = words.length >= 2 && words.every(word => hasLetterPattern.test(word));
      
      if (isValid) {
        const firstName = words[0];
        const lastName = words[words.length - 1];
        const lastInitial = lastName.match(/[a-zA-Z]/)?.[0]?.toUpperCase() || '';
        setPreviewText(`Customer sees: ${firstName} ${lastInitial}.`);
      } else {
        setPreviewText(null);
      }
    } else {
      setPreviewText(null);
    }
  };

  // Re-validate the phone field when the COUNTRY changes — but not on first mount, so we
  // don't greet the user with errors on fields they haven't touched yet.
  const skipInitialPhoneValidate = useRef(true);
  useEffect(() => {
    if (skipInitialPhoneValidate.current) { skipInitialPhoneValidate.current = false; return; }
    const timer = setTimeout(() => {
      form.validateFields(['phoneNumber']).catch(() => {});
    }, 100);
    return () => clearTimeout(timer);
  }, [form, selectedCountry]);

  // Initialize preview text on mount
  useEffect(() => {
    const displayName = form.getFieldValue('displayName');
    const country = form.getFieldValue('country') || selectedCountry;
    updatePreviewText(displayName, country);
    if (isSignUp) setSignupReady(isSignupFormReady(form.getFieldsValue()));
  }, [form, selectedCountry, isCompanyChecked]);

  // Re-validate name/dob when country or company-type changes — but not on first mount
  // (avoid pre-showing "You need to be 13+" etc. before the user has entered anything).
  const skipInitialNameDobValidate = useRef(true);
  useEffect(() => {
    if (skipInitialNameDobValidate.current) { skipInitialNameDobValidate.current = false; return; }
    if (!isCompanyChecked) {
      const timer = setTimeout(() => {
        form.validateFields(isSignUp ? ['displayName'] : ['displayName', 'dob']).catch(() => {});
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [form, selectedCountry, isCompanyChecked]);

  // New clients must have an OTP-verified mobile before completing signup (anti-fraud).
  // Admins are exempt (they set a contact-only number). Existing-client edits are unaffected.
  const phoneVerificationRequired = !!isSignUp && !isAdmin && !verifiedAuthPhone;

  const onFinish = (values) => {
    if (phoneVerificationRequired) {
      return; // guard: submit is blocked until the mobile is verified
    }
    // initialValues seeds the store with every client key, including ones whose
    // field is not mounted on this form (dob at sign-up). Firestore rejects an
    // explicit undefined, so only send what has a value.
    Object.keys(values).forEach((key) => { if (values[key] === undefined) delete values[key]; });
    if (values.categoryCode) values['categoryName'] = categoriesEN[values.categoryCode];

    if (values.dob) {
      const firebase = firebaseSetup.getFirebase();
      const dobMoment = values.dob;
      values.dob = firebase.firestore.Timestamp.fromDate(new Date(dobMoment.valueOf()));
    }

    if (callback) {
      callback(values);
    }
  };

  const onSelectCountry = (value) => {
    setSelectedCountry(value);
    // Update preview text when country changes
    const displayName = form.getFieldValue('displayName');
    updatePreviewText(displayName, value);
    // Re-validate phone number when country changes
    setTimeout(() => {
      form.validateFields(['phoneNumber']).catch(() => {
        // Ignore validation errors - just trigger the validation display
      });
    }, 50);
  };

  const placeHolderPhone = () => {
    if (!selectedCountry) return phonePlaceHolderByCountryCode['US'];
    return phonePlaceHolderByCountryCode[selectedCountry];
  }

  // Phone validation patterns by country
  const phoneValidationPatterns = {
    'US': /^(\+1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,  // US: +1 (555) 123-4567 or 5551234567
    'IE': /^(\+353|0)\s?(?:[1-9]\d{0,2})\s?(?:\d[-.\s]?){5,7}$/,  // Ireland: +353 88 123 4567 353 45 54 387
    'GB': /^(\+44|0)?[-.\s]?[1-9]\d{1,4}[-.\s]?\d{3,6}[-.\s]?\d{3,4}$/,  // UK: +44 1234 567890
    // EU countries - general pattern for most EU formats
    'AT': /^(\+43|0)?[-.\s]?[1-9]\d{1,4}[-.\s]?\d{3,10}$/,
    'BE': /^(\+32|0)?[-.\s]?[1-9]\d{1,2}[-.\s]?\d{5,7}$/,
    'CY': /^(\+357)?[-.\s]?[2-9]\d{7}$/,
    'EE': /^(\+372)?[-.\s]?[3-9]\d{6,7}$/,
    'FI': /^(\+358|0)?[-.\s]?[1-9]\d{5,10}$/,
    'DE': /^(\+49|0)?[-.\s]?[1-9]\d{1,14}$/,
    'GR': /^(\+30)?[-.\s]?[2-9]\d{9}$/,
    'IT': /^(\+39)?[-.\s]?[1-9]\d{5,11}$/,
    'LV': /^(\+371)?[-.\s]?[2-9]\d{7}$/,
    'LT': /^(\+370|8)?[-.\s]?[3-9]\d{7}$/,
    'LU': /^(\+352)?[-.\s]?[2-9]\d{5,10}$/,
    'MT': /^(\+356)?[-.\s]?[2-9]\d{7}$/,
    'SK': /^(\+421|0)?[-.\s]?[1-9]\d{8}$/,
    'SI': /^(\+386|0)?[-.\s]?[1-9]\d{7,8}$/,
  };

  // Normalize any date value to moment for Ant Design DatePicker (expects moment, not Firestore Timestamp)
  const toMoment = (val) => {
    if (!val) return undefined;
    if (moment.isMoment(val)) return val;
    if (typeof val?.toMillis === 'function') return moment(val.toMillis());
    return moment(val);
  };

  const stableClientId = client?.clientId ?? client?.id;
  let formInitialValues;
  if (!stableClientId) {
    initialValuesSeedRef.current = { id: null, values: null };
    formInitialValues = undefined;
  } else if (initialValuesSeedRef.current.id === stableClientId) {
    formInitialValues = initialValuesSeedRef.current.values;
  } else {
    formInitialValues = {
      ...client,
      dob: toMoment(client.dob),
      // Notifications Email defaults to the priority login email when the user hasn't
      // chosen one ('' = an explicit "None"; a chosen value is kept as-is).
      email: client.email != null ? client.email : (client.authEmail || ''),
    };
    initialValuesSeedRef.current = { id: stableClientId, values: formInitialValues };
  }

  // Notifications Email options: the account's verified sign-in emails + None (+ a
  // grandfathered custom value if one is already set and isn't one of them).
  const verifiedEmails = Array.from(new Set(((client && client.authEmails) || []).filter(Boolean)));
  const currentNotifEmail = client && client.email;
  const notificationEmailOptions = [
    { label: "None — don't send me emails", value: '' },
    ...verifiedEmails.map((e) => ({ label: e, value: e })),
    ...(currentNotifEmail && !verifiedEmails.includes(currentNotifEmail)
      ? [{ label: `${currentNotifEmail} (current)`, value: currentNotifEmail }]
      : []),
  ];

  const getPhoneValidationMessage = () => {
    if (!selectedCountry) return 'Please enter a valid phone number';
    
    const messages = {
      'US': 'Please enter a valid US phone number (e.g., 650-555-1234 or +1-650-555-1234)',
      'IE': 'Please enter a valid Irish phone number (e.g., 088 123 4567 or +353 88 123 4567 or 353 45 54 387)',
      'GB': 'Please enter a valid UK phone number (e.g., 01234 567890 or +44 1234 567890)',
      'AT': 'Please enter a valid Austrian phone number',
      'BE': 'Please enter a valid Belgian phone number',
      'CY': 'Please enter a valid Cypriot phone number',
      'EE': 'Please enter a valid Estonian phone number',
      'FI': 'Please enter a valid Finnish phone number',
      'FR':
        'French numbers need 9 digits after +33 (e.g. +33 6 12 34 56 78 or 06 12 34 56 78). Spaces are OK.',
      'DE': 'Please enter a valid German phone number',
      'GR': 'Please enter a valid Greek phone number',
      'IT': 'Please enter a valid Italian phone number',
      'LV': 'Please enter a valid Latvian phone number',
      'LT': 'Please enter a valid Lithuanian phone number',
      'LU': 'Please enter a valid Luxembourgish phone number',
      'MT': 'Please enter a valid Maltese phone number',
      'NL':
        'Dutch numbers need 9 digits after +31 (e.g. +31 6 12345678 or 06 12345678). Spaces are OK.',
      'PT':
        'Portuguese numbers need 9 digits after +351 (e.g. +351 912 345 678). Spaces are OK.',
      'SK': 'Please enter a valid Slovak phone number',
      'SI': 'Please enter a valid Slovenian phone number',
      'ES':
        'Spanish numbers need 9 digits after +34, starting with 6–9 (e.g. +34 612 345 678 or 612 345 678). Check you did not add an extra digit after +34.',
    };
    
    return messages[selectedCountry] || 'Please enter a valid phone number';
  };

  return (
    <Form
      {...(isSignUp ? { layout: 'vertical', size: 'large' } : FormItemLayout)}
      form={form}
      name="register"
      onFinish={onFinish}
      onValuesChange={(changedValues, allValues) => {
        // Update preview text when displayName or country changes
        const displayName = allValues.displayName;
        const country = allValues.country || selectedCountry;
        updatePreviewText(displayName, country);
        if (isSignUp) setSignupReady(isSignupFormReady(allValues));
      }}
      initialValues={formInitialValues}
      scrollToFirstError
    >

      <>
        <Form.Item
          name={'country'}
          rules={[{ required: notRequiredWhenAdmin({ isAdmin, required: true }), message: 'Country is required' }]}
          label={
            <span>
              Your Country&nbsp;
              <Tooltip title="Please enter a country">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          {isSignUp ? (
            // The browser's own picker on a phone; the number's country is only
            // a default — this is where they live and get paid.
            <NativeSelect
              ariaLabel="Country of residence"
              placeholder="Where you live"
              onChange={(code) => { if (code) onSelectCountry(code); }}
              options={availableCountryCodes.map((code) => ({ value: code, label: countryByCountryCode[code] }))}
            />
          ) : (
            <Select
              placeholder="Country of residence"
              onSelect={onSelectCountry}
            >
              {
                availableCountryCodes.map(
                  (code) => (
                    <Option key={code} value={code}>{countryByCountryCode[code]}</Option>
                  )
                )
              }
            </Select>
          )}
        </Form.Item>
      </>

      {false && countriesNotSupportingBusiness.includes(selectedCountry) &&
        <Form.Item
          name="isCompany"
          valuePropName="checked"
          rules={[
            {
              required: false,
            },
          ]}
          {...TailFormItemLayout}
        >
          <Checkbox onChange={(v) => (setIsCompanyChecked(!isCompanyChecked))}>
            Register as Business
          </Checkbox>
        </Form.Item>
      }

      {isSignUp ? (
        // A tappable grid, not a dropdown: a dropdown's inner scroll misbehaves on
        // some Android browsers, and sixteen roles fit on the screen whole.
        <Form.Item name="categoryCode" label={isCompanyChecked ? 'Business type' : 'What do you do?'}>
          <CategoryPillGrid isCompany={isCompanyChecked} />
        </Form.Item>
      ) : (
        <ClientCategoryFormItem fieldName='categoryCode' category={client.categoryCode} isCompany={isCompanyChecked} />
      )}

      {!isCompanyChecked &&
        <>
          <Form.Item
            name="displayName"
            dependencies={['country']}
            label={
              <span>
                Full Name&nbsp;
                <Tooltip title="Please enter your full name.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            rules={[
              {
                required: notRequiredWhenAdmin({ isAdmin, required: true }),
                message: 'Use your full name. This will display as first name and first initial!',
                whitespace: true,
              },
            // Validation: must be two or more words, each word one or more letters (applies to all countries)
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve(); // Required check already handled above
                // Split by whitespace and filter out empty strings
                const words = value.trim().split(/\s+/).filter(word => word.length > 0);
                // Check if we have at least 2 words
                if (words.length < 2) {
                  return Promise.reject('We need first and last name');
                }
                // Check if each word has at least one letter (allows hyphens, apostrophes, etc.)
                const hasLetterPattern = /[a-zA-Z]/;
                const allWordsHaveLetters = words.every(word => hasLetterPattern.test(word));
                if (!allWordsHaveLetters) {
                  return Promise.reject('Each word in your full name must contain at least one letter');
                }
                return Promise.resolve();
              },
            },
            ]}
            help={previewText ? <span style={{ color: '#52c41a' }}>{previewText}</span> : undefined}
          >
            <Input
              placeholder="Full given name"
            />
          </Form.Item>
        </>
      }

      {isCompanyChecked &&
        <Form.Item
          name="displayName"
          label={
            <span>
              Full Company Name&nbsp;
              <Tooltip title="Please enter your business name.">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
          rules={[
            {
              required: notRequiredWhenAdmin({ isAdmin, required: true }),
              message: 'Use your full business name.',
              whitespace: true,
            },
          ]}
        >
          <Input
            placeholder="ACME Ltd."
          />
        </Form.Item>
      }

      {isShowAllFields &&
        <Form.Item
          name="profileBio"
          label={
            <span>
              Your presonal message&nbsp;
              <Tooltip title="Add your personal message when you get tipped. This will be displayed when you get tipped.">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
          rules={[
            {
              required: false,
              message: 'Please input a short tag line!',
              whitespace: true,
            },
          ]}
        >
          <Input
            placeholder="Best of the best"
          />
        </Form.Item>
      }


      {/* Any phone a CLIENT provides must be OTP-verified — never persisted unverified.
          So whenever a non-admin client has no verified phone, we show the SMS-verify flow
          (signup AND profile edit), not a free-text field. On signup it's REQUIRED (submit
          blocked until verified); on profile edit it's optional but verify-only. Admins keep
          the contact-only free-text field below. (Anti-fraud.) */}
      {!isAdmin && !verifiedAuthPhone &&
        <Form.Item label="Mobile number" required={!!isSignUp}>
          <SignupPhoneVerify
            onVerified={onPhoneVerified}
            profileCountry={client && client.country}
            initialPhone={client && client.phoneNumber && `${client.phoneNumber}`.startsWith('+')
              ? client.phoneNumber : undefined}
          />
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            {isSignUp
              ? 'We verify your mobile by SMS to secure your account. This is required to continue.'
              : 'We verify your mobile by SMS. Any number you add must be verified to secure your account.'}
          </div>
        </Form.Item>
      }

      {(verifiedAuthPhone || isAdmin) &&
        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={verifiedAuthPhone ? [] : [
            {
              required: notRequiredWhenAdmin({ isAdmin, required: true }),
              message: 'We use your phone number to help recover your account',
            },
            PHONE_DIGIT_VALIDATORS[selectedCountry]
              ? {
                  validator: (_, value) => {
                    if (value === undefined || value === null || `${value}`.trim() === '') {
                      return Promise.resolve();
                    }
                    const check = PHONE_DIGIT_VALIDATORS[selectedCountry];
                    return check(`${value}`)
                      ? Promise.resolve()
                      : Promise.reject(new Error(getPhoneValidationMessage()));
                  },
                }
              : {
                  pattern: selectedCountry ? phoneValidationPatterns[selectedCountry] : phoneValidationPatterns.US,
                  message: getPhoneValidationMessage(),
                },
          ]}
          extra={verifiedAuthPhone ? (
            <span style={{ fontSize: 12 }}>
              Verified via SMS.{' '}
              {!isSignUp && (
                <a href={window.location.pathname.replace(/\/profile.*/, '/security')}>
                  Change via Security page
                </a>
              )}
            </span>
          ) : (isAdmin ? (
            <span style={{ fontSize: 12 }}>
              Contact number only — saving here does not enable phone sign-in.
            </span>
          ) : undefined)}
        >
          <Input
            placeholder={verifiedAuthPhone ? '' : placeHolderPhone()}
            disabled={!!verifiedAuthPhone}
            suffix={verifiedAuthPhone ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null}
            style={{ width: '100%' }}
          />
        </Form.Item>
      }

      {/* Date of birth belongs to the payout account, not the sign-up. It is asked
          on the Payouts screen before the first bank account (PayoutDetailsForm),
          which is where Stripe first needs it. */}
      {!isSignUp &&
        <Form.Item
          name="dob"
          dependencies={['country', 'isCompany']}
          label={isCompanyChecked ? 'Founded on' : 'Date of Birth'}
          getValueProps={(value) => ({ value: toMoment(value) })}
          rules={[
            {
              required: notRequiredWhenAdmin({ isAdmin, required: true }),
              message: isCompanyChecked ? 'Founded on is required' : 'You need to be 13+',
            },
            // Validation: must be 13 or older (applies to all countries for non-company accounts)
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve(); // Required check already handled above
                // Check age for non-company accounts (isCompany is from state, not form)
                if (!isCompanyChecked) {
                  const dobMoment = toMoment(value);
                  const today = moment();
                  const age = today.diff(dobMoment, 'years', true);
                  if (age < 13) {
                    return Promise.reject('You must be 13+');
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {/* Default picker to Jan 1 1990 */}
          <DatePicker allowClear={false} defaultPickerValue={moment(631152000000)} format={"DD/MM/YYYY"} />
        </Form.Item>
      }

      {isShowAllFields &&
        <Form.Item
          name="employerName"
          label={isCompanyChecked ? 'Trading as' : 'Employer Name'}
          rules={[
            {
              required: false,
              message: 'We need to know where you work',
            },
          ]}
        >
          <Input
            placeholder={isCompanyChecked ? 'Trading as name' : 'Tell us where you work'}
          />
        </Form.Item>
      }

      {isShowAllFields &&
        <Form.Item
          name="email"
          label={
            <span>
              Notifications Email&nbsp;
              <Tooltip title="Where we send your notifications. Choose one of your verified sign-in emails, or None. This is separate from the email you use to log in.">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
        >
          <Select
            options={notificationEmailOptions}
            placeholder="Choose a verified email or None"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      }

    { !isSignUp && countriesRequiringSSN.includes(selectedCountry) &&
        <Form.Item
          name="idLast4"
          dependencies={['country']}
          label={
            <span>
              Last four digits of your SSN&nbsp;
              <Tooltip title="We only need the last 4 digits of your Social Security Number for verification purposes. The first 5 digits are masked for security.">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
          rules={[
            {
              validator: (_, value) => {
                // Only validate for US clients
                const formCountry = form.getFieldValue('country') || selectedCountry;
                if (formCountry !== 'US') {
                  return Promise.resolve();
                }
                if (!value) {
                  return Promise.reject('SSN last 4 digits must be provided');
                }
                if (!/^\d{4}$/.test(value)) {
                  return Promise.reject('SSN last 4 must be exactly four digits');
                }
                return Promise.resolve();
              },
            },
          ]}


        >
          <SsnLast4Input />
        </Form.Item>
      }


      <Form.Item
        name="agreeTerms"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              (value || isAdmin) ? Promise.resolve() : Promise.reject('Please accept terms'),
          },
        ]}
        {...(isSignUp ? {} : TailFormItemLayout)}
      >
        <Checkbox style={isSignUp ? { fontSize: 16 } : undefined}>
          {isSignUp ? 'I’m 13 or older and I accept the ' : 'I accept the '}
          <a href="https://www.strikepay.co/eula" target="_blank" rel="noreferrer">terms.</a>
        </Checkbox>
      </Form.Item>

      {!isSignUp && <Form.Item
        name="marketingOptIn"
        valuePropName="checked"
        rules={[
          {
            required: false,
          },
        ]}
        {...TailFormItemLayout}
      >
        <Checkbox>
          Allow Strikepay to send important communications.
        </Checkbox>
      </Form.Item>}

      <AdminCheckBoxFormItems isAdmin={isAdmin} title="Admin Only Settings" />

      <Form.Item {...(isSignUp ? {} : TailFormItemLayout)}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={phoneVerificationRequired || (!!isSignUp && !signupReady)}
          block={!!isSignUp}
          style={isSignUp ? { height: 52, fontSize: 18, fontWeight: 600, borderRadius: 8 } : undefined}
        >
          {isSignUp ? 'Continue' : 'Save'}
        </Button>
        {phoneVerificationRequired && (
          <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 8 }}>
            Please verify your mobile number above to continue.
          </div>
        )}
      </Form.Item>
    </Form>
  );
};